import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { useJsonLd } from '../hooks/useJsonLd'
import { buildServicePageGraph } from '../lib/schema'
import { Button } from '@/components/ui/button'

const Services = () => {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const location = useLocation()
  const [currentQuestion, setCurrentQuestion] = useState(0)

  const pageTitle = `${t('services.hero.title')} | VIVA Psychotherapy`
  const pageDescription =
    t('home.seo.servicesRoute.description') ||
    t('services.hero.subtitle') ||
    t('services.hero.description')

  usePageSeo({
    enabled: !isLoading,
    title: pageTitle,
    description: pageDescription,
    keywords: t('home.seo.servicesRoute.keywords'),
    pathname: location.pathname
  })

  const servicesSchema = useMemo(
    () =>
      buildServicePageGraph({
        pathname: location.pathname,
        pageName: pageTitle,
        pageDescription,
        serviceName: t('services.hero.title'),
        serviceDescription: t('services.hero.subtitle') || t('services.hero.description'),
        faqItems: [
          {
            question: t('services.faq.insurance.question'),
            answer: t('services.faq.insurance.answer')
          },
          {
            question: t('services.faq.payment.question'),
            answer: t('services.faq.payment.answer')
          },
          {
            question: t('services.faq.contact.question'),
            answer: t('services.faq.contact.answer')
          }
        ]
      }),
    [location.pathname, t, pageTitle, pageDescription, isLoading]
  )

  useJsonLd('data-services-schema', servicesSchema, !isLoading)

  // FAQ data with translations
  const faqData = [
    {
      question: t('services.faq.insurance.question'),
      answer: t('services.faq.insurance.answer'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      question: t('services.faq.payment.question'),
      answer: t('services.faq.payment.answer'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      )
    },
    {
      question: t('services.faq.contact.question'),
      answer: t('services.faq.contact.answer'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      )
    }
  ]

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % faqData.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  // Show loading state if translations are still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite" data-seo-ready="1">
      <main>
        <section className="bg-slate-700 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('services.hero.title')}
            </h1>
            <p className="text-xl text-primary-lightGray mb-4 max-w-3xl mx-auto">
              {t('services.hero.subtitle')}
            </p>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              {t('services.hero.description')}
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Individual Counseling */}
              <div className="group bg-white border border-gray-200 shadow-sm rounded-xl hover:shadow-md transition-all duration-300">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    className="w-full h-48 object-cover rounded-t-xl group-hover:scale-105 transition-transform duration-300" 
                    src="/img/random/pexels-photo-4101143-4101143-768x512.jpg" 
                    alt={t('services.services.individual.title')}
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h2 className="text-xl font-semibold text-gray-800 group-hover:text-primary-darkGreen transition-colors duration-300">
                    {t('services.services.individual.title')}
                  </h2>
                  <div className="mt-3 text-gray-600 space-y-3 text-justify">
                    {(t('services.services.individual.description') || []).map((paragraph, index) => (
                      <p key={index}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm text-gray-600 text-justify">
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{t('services.services.individual.duration')}</span>
                    </div>
                    <div className="flex items-center text-sm font-semibold text-primary-darkGreen text-justify">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span>{t('services.services.individual.rate')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Workshops and Professional Consultation */}
              <div className="group bg-white border border-gray-200 shadow-sm rounded-xl hover:shadow-md transition-all duration-300">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    className="w-full h-48 object-cover rounded-t-xl group-hover:scale-105 transition-transform duration-300" 
                    src="/img/random/pexels-photo-3184328-3184328-scaled.jpg" 
                    alt={t('services.services.workshops.title')}
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h2 className="text-xl font-semibold text-gray-800 group-hover:text-primary-darkGreen transition-colors duration-300">
                    {t('services.services.workshops.title')}
                  </h2>
                  <div className="mt-3 text-gray-600 space-y-3 text-justify">
                    {(t('services.services.workshops.description') || []).map((paragraph, index) => (
                      <p key={index}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm text-gray-600 text-justify">
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{t('services.services.workshops.duration')}</span>
                    </div>
                    <div className="flex items-center text-sm font-semibold text-primary-darkGreen text-justify">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span>{t('services.services.workshops.rate')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Group Counseling */}
              <div className="group bg-white border border-gray-200 shadow-sm rounded-xl hover:shadow-md transition-all duration-300">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    className="w-full h-48 object-cover rounded-t-xl group-hover:scale-105 transition-transform duration-300" 
                    src="/img/random/pexels-photo-7176305-7176305-768x512.jpg" 
                    alt={t('services.services.group.title')}
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h2 className="text-xl font-semibold text-gray-800 group-hover:text-primary-darkGreen transition-colors duration-300">
                    {t('services.services.group.title')}
                  </h2>
                  <div className="mt-3 text-gray-600 space-y-3 text-justify">
                    {(t('services.services.group.description') || []).map((paragraph, index) => (
                      <p key={index}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm text-gray-600 text-justify">
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{t('services.services.group.duration')}</span>
                    </div>
                    <div className="flex items-center text-sm font-semibold text-primary-darkGreen text-justify">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span>{t('services.services.group.rate')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Insurance Coverage Section */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-darkGreen mb-4">
                {t('services.insurance.title')}
              </h2>
              <p className="text-lg text-gray-700 mb-6 max-w-3xl mx-auto">
                {t('services.insurance.partnership')}
              </p>
            </div>

            {/* Insurance Provider Logos Grid */}
            <div className="grid grid-cols-3 gap-6 md:gap-8 mb-12">
              {t('services.insurance.providers').map((provider, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-primary-darkGreen/30 hover:shadow-md transition-all duration-300 group"
                >
                  <img
                    src={provider.logo}
                    alt={provider.name}
                    className="max-h-20 max-w-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300 opacity-75 group-hover:opacity-100"
                    title={provider.name}
                  />
                </div>
              ))}
            </div>

            {/* Headway Action Button and Coverage Note */}
            <div className="space-y-4 text-center">
              <Button asChild variant="brand">
                <a
                  href="https://care.headway.co/providers/lidiana-cartolano"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('services.insurance.verifyButton')}
                </a>
              </Button>
              <p className="mx-auto max-w-2xl text-sm italic text-gray-600">
                {t('services.insurance.coverageNote')}
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Carousel */}
        <section className="bg-primary-lightGray py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              {/* Carousel Container */}
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentQuestion * 100}%)` }}
                >
                  {faqData.map((faq, index) => (
                    <div key={index} className="w-full flex-shrink-0">
                      <div className="text-center">
                        {/* Icon */}
                        <div className="flex justify-center mb-6">
                          <div className="w-16 h-16 bg-primary-darkGreen rounded-full flex items-center justify-center text-white">
                            {faq.icon}
                          </div>
                        </div>
                        
                        {/* Question */}
                        <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-6">
                          {faq.question}
                        </h2>
                        
                        {/* Answer */}
                        <p className="text-lg text-primary-darkGray mb-8 max-w-2xl mx-auto">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center space-x-2 mt-8">
                {faqData.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentQuestion(index)}
                    className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                      index === currentQuestion 
                        ? 'bg-primary-darkGreen' 
                        : 'bg-primary-darkGreen/30 hover:bg-primary-darkGreen/50'
                    }`}
                    aria-label={`Go to question ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default Services
